import { Observable } from 'rxjs'
import { io as IO } from 'socket.io-client'

/**
 * Options for connecting to a Socket.IO realtime event stream.
 */
export interface SocketIoOptions {
  /**
   * The API base URL.
   */
  readonly baseURL: string

  /**
   * The token to use.
   */
  readonly token: string

  /**
   * URL search params to add to the URL. Optional.
   */
  readonly params?: URLSearchParams
}

/**
 * Connects to a stream of events using Socket.IO.
 * @param options
 */
export function connectToSocketIoRealtimeEvents(options: SocketIoOptions) {
  return new Observable<RealtimeEventEnvelope>((observer) => {
    const url = new URL(options.baseURL)
    const pathname = url.pathname.endsWith('/')
      ? url.pathname.slice(0, -1)
      : url.pathname

    try {
      const io = IO(url.origin, {
        path: `${pathname}/socket.io${options.params ? `?${options.params?.toString()}` : ''}`,
        transports: ['websocket'],
        reconnection: false,
      })
      io.on('event', (evt: RealtimeEventEnvelope) => {
        observer.next(evt)
      })
      io.on('connect_error', (e) => {
        observer.error(e)
      })
      io.on('disconnect', () => {
        io.disconnect()
        observer.complete()
      })
      io.once('connect', () => {
        io.emit(
          'authenticate',
          { token: options.token },
          (result: { success: boolean; data: unknown }) => {
            if (result.success) {
              return
            }

            console.error('Could not authenticate with event server: ', result)
            observer.error(
              new Error('Could not authenticate with realtime event server'),
            )
          },
        )
      })
      return () => {
        io.close()
      }
    } catch (e: unknown) {
      observer.error(e)
    }
  })
}

/**
 * Envelope used for realtime events.
 */
export interface RealtimeEventEnvelope {
  type: string
  data: unknown
}
